import React from "react";
import AppShell from "../layout/AppShell";
import { listUsersPage, type UserListItem } from "../services/usersService";
import { adminSetUserRole } from "../services/adminApi";
import { useAuthStore } from "../stores/authStore";
import RoleBadge from "../components/RoleBadge";
import RoleSelect from "../components/RoleSelect";
import RoleFilter from "../components/RoleFilter";
import UserEditorDrawer from "../components/UserEditorDrawer";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  type Role,
  parseRole,
  canManageRole,
  canAssignRole,
  OWNER_EMAIL,
} from "../models/auth";
import type { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import {
  MdPeople,
  MdSearch,
  MdFilterList,
  MdRefresh,
  MdEdit,
  MdDoneAll,
  MdClose,
  MdLock,
} from "react-icons/md";

import {
  getFirestore,
  collection,
  query as fsQuery,
  where,
  orderBy,
  limit,
  onSnapshot,
  type QueryConstraint,
} from "firebase/firestore";

export default function Users(): React.ReactElement {
  const actorRole = useAuthStore((s) => s.state.profile?.role ?? "user");

  const [rows, setRows] = React.useState<UserListItem[]>([]);
  const [cursor, setCursor] = React.useState<QueryDocumentSnapshot<
    DocumentData,
    DocumentData
  > | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [err, setErr] = React.useState<string | null>(null);

  const [query, setQuery] = React.useState<string>("");
  const [roleFilter, setRoleFilter] = React.useState<Role | null>(null);

  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [selectedUser, setSelectedUser] = React.useState<UserListItem | null>(
    null
  );

  const [liveHead, setLiveHead] = React.useState<Map<string, UserListItem>>(
    () => new Map()
  );

  const isOwnerRow = (u: UserListItem) =>
    typeof u.email === "string" &&
    u.email.toLowerCase() === OWNER_EMAIL.toLowerCase();

  const uiCanEditRow = (u: UserListItem): boolean => {
    if (isOwnerRow(u)) return false;
    return canManageRole(actorRole, u.role ?? "user");
  };

  const uiCanChangeTo = (u: UserListItem, next: Role): boolean => {
    if (!uiCanEditRow(u)) return false;
    return canAssignRole(actorRole, next);
  };

  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const toggleRow = (uid: string, target?: UserListItem) =>
    setSelectedIds((prev) => {
      const u = target ?? mergedRows.find((r) => r.uid === uid);
      if (!u || !uiCanEditRow(u)) return prev;
      const s = new Set(prev);
      s.has(uid) ? s.delete(uid) : s.add(uid);
      return s;
    });
  const clearSelection = () => setSelectedIds(new Set());

  const load = React.useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const opts = roleFilter !== null ? { role: roleFilter } : undefined;
        const page = await listUsersPage(
          20,
          reset ? undefined : cursor ?? undefined,
          opts
        );
        setRows((r) => (reset ? page.items : [...r, ...page.items]));
        setCursor(page.next);
        setErr(null);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [cursor, roleFilter]
  );

  React.useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    clearSelection();
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter]);

  // live head
  React.useEffect(() => {
    const db = getFirestore();
    const cons: QueryConstraint[] = [];
    if (roleFilter !== null) cons.push(where("role", "==", roleFilter));

    const qHead = fsQuery(
      collection(db, "users"),
      ...cons,
      orderBy("createdAt", "desc"),
      limit(10)
    );
    const qNull = fsQuery(
      collection(db, "users"),
      where("createdAt", "==", null),
      ...(roleFilter !== null ? [where("role", "==", roleFilter)] : []),
      orderBy("updatedAt", "desc"),
      limit(5)
    );

    const applySnapshot = (snap: any) => {
      setLiveHead((prev) => {
        const next = new Map(prev);
        snap.docChanges().forEach((ch: any) => {
          const d = ch.doc.data() as any;
          const role = parseRole(d.role) ?? "user";
          const item: UserListItem = {
            uid: ch.doc.id,
            email: String(d.email ?? ""),
            fullName: typeof d.fullName === "string" ? d.fullName : undefined,
            role,
          };
          if (ch.type === "removed") next.delete(item.uid);
          else next.set(item.uid, item);
        });
        return next;
      });
    };

    const unsub1 = onSnapshot(qHead, applySnapshot);
    const unsub2 = onSnapshot(qNull, applySnapshot);
    return () => {
      unsub1();
      unsub2();
    };
  }, [roleFilter]);

  const mergedRows = React.useMemo<UserListItem[]>(() => {
    const m = new Map<string, UserListItem>();
    for (const u of Array.from(liveHead.values())) m.set(u.uid, u);
    for (const u of rows) if (!m.has(u.uid)) m.set(u.uid, u);
    return Array.from(m.values());
  }, [liveHead, rows]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return mergedRows;
    return mergedRows.filter((u) => {
      const name = (u.fullName ?? "").toLowerCase();
      const email = (u.email ?? "").toLowerCase();
      return (
        email.includes(q) || name.includes(q) || u.uid.toLowerCase().includes(q)
      );
    });
  }, [query, mergedRows]);

  const onRoleChangeOne = async (uid: string, role: Role | null) => {
    const target = mergedRows.find((x) => x.uid === uid);
    if (!target) return;
    if (!uiCanEditRow(target)) {
      alert("No tienes permisos para editar este usuario.");
      return;
    }
    const nextRole = role ?? "user";
    if (!uiCanChangeTo(target, nextRole)) {
      alert("No puedes asignar ese rol.");
      return;
    }
    try {
      await adminSetUserRole(uid, nextRole);
      setLiveHead((prev) => {
        const next = new Map(prev);
        if (next.has(uid)) next.set(uid, { ...next.get(uid)!, role: nextRole });
        return next;
      });
      setRows((r) =>
        r.map((x) => (x.uid === uid ? { ...x, role: nextRole } : x))
      );
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const [bulkRole, setBulkRole] = React.useState<Role | null>(null);
  const [bulkBusy, setBulkBusy] = React.useState<boolean>(false);

  const applyBulkRole = async () => {
    if (!bulkRole) return;
    setBulkBusy(true);

    const ids = Array.from(selectedIds).filter((id) => {
      const u = filtered.find((x) => x.uid === id);
      return u && uiCanEditRow(u) && uiCanChangeTo(u, bulkRole);
    });

    if (ids.length === 0) {
      setBulkBusy(false);
      alert("No hay usuarios seleccionados que puedas actualizar a ese rol.");
      return;
    }

    const CHUNK = 5;
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        await Promise.all(slice.map((uid) => adminSetUserRole(uid, bulkRole)));
        setLiveHead((prev) => {
          const next = new Map(prev);
          slice.forEach((uid) => {
            if (next.has(uid))
              next.set(uid, { ...next.get(uid)!, role: bulkRole });
          });
          return next;
        });
        setRows((r) =>
          r.map((x) => (slice.includes(x.uid) ? { ...x, role: bulkRole } : x))
        );
      }
      clearSelection();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBulkBusy(false);
    }
  };

  const openDrawer = (u: UserListItem) => {
    if (!uiCanEditRow(u)) {
      alert("Este usuario no puede editarse por tu nivel de permisos.");
      return;
    }
    setSelectedUser(u);
    setDrawerOpen(true);
  };

  const allVisibleSelected =
    filtered.length > 0 &&
    filtered.every((u) => selectedIds.has(u.uid) || !uiCanEditRow(u));

  const toggleSelectVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const s = new Set(prev);
        filtered.forEach((u) => {
          if (uiCanEditRow(u)) s.delete(u.uid);
        });
        return s;
      });
    } else {
      setSelectedIds((prev) => {
        const s = new Set(prev);
        filtered.forEach((u) => {
          if (uiCanEditRow(u)) s.add(u.uid);
        });
        return s;
      });
    }
  };

  return (
    <AppShell>
      <section className="card p-4">
        {/* Barra superior */}
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <span className="icon-accent text-2xl">
              <MdPeople />
            </span>
            <h2 className="text-lg font-semibold">Usuarios</h2>
            <span className="badge-tonal ml-2">{filtered.length} visibles</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <MdSearch className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-on-surface/60" />
              <input
                aria-label="Buscar usuarios"
                className="input-surface w-64 pl-8"
                placeholder="Buscar por email/nombre/UID…"
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
              />
            </div>

            <div className="toolbar-tonal flex items-center gap-2 px-2 py-1">
              <MdFilterList className="text-on-surface/70" />
              <RoleFilter value={roleFilter} onChange={setRoleFilter} />
            </div>

            <button
              className="btn btn-outline flex items-center gap-1"
              onClick={() => load(true)}
              disabled={loading}
              title="Recargar"
            >
              <MdRefresh /> Recargar
            </button>
          </div>
        </div>

        {/* Acciones en lote */}
        {selectedIds.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-3 toolbar-tonal px-3 py-2 text-sm">
            <span className="text-onSecondaryContainer">
              {selectedIds.size} seleccionados
            </span>
            <RoleSelect value={bulkRole} onChange={setBulkRole} />
            <button
              className="btn btn-primary flex items-center gap-1"
              onClick={applyBulkRole}
              disabled={bulkBusy || bulkRole === null}
            >
              {bulkBusy ? (
                "Aplicando…"
              ) : (
                <>
                  <MdDoneAll /> Cambiar rol
                </>
              )}
            </button>
            <button
              className="btn btn-outline flex items-center gap-1"
              onClick={clearSelection}
              disabled={bulkBusy}
            >
              <MdClose /> Limpiar
            </button>
          </div>
        )}

        {err && <p className="mb-3 text-sm text-red-600">{err}</p>}

        <div className="overflow-x-auto rounded-lg border border-surface">
          <table className="table-tonal text-sm">
            <thead>
              <tr>
                <th className="px-3 py-2">
                  <input
                    aria-label="Seleccionar filas visibles"
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectVisible}
                  />
                </th>
                <Th>UID</Th>
                <Th>Email</Th>
                <Th>Nombre</Th>
                <Th>Rol</Th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading && mergedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8">
                    <LoadingSpinner label="Cargando usuarios…" />
                  </td>
                </tr>
              ) : (
                filtered.map((u) => {
                  const checked = selectedIds.has(u.uid);
                  const locked = !uiCanEditRow(u);
                  const rowIsOwner = isOwnerRow(u);
                  return (
                    <tr
                      key={u.uid}
                      className={checked ? "table-row-selected" : ""}
                      title={
                        rowIsOwner
                          ? "Owner (bloqueado)"
                          : locked
                          ? "Sin permisos para este usuario"
                          : undefined
                      }
                    >
                      <td className="px-3 py-2 align-top">
                        <input
                          aria-label={`Seleccionar ${u.email}`}
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRow(u.uid, u)}
                          disabled={locked}
                        />
                      </td>
                      <Td>
                        <code className="rounded bg-black/5 px-2 py-0.5 text-xs dark:bg-white/10">
                          {u.uid.slice(0, 8)}…
                        </code>
                      </Td>
                      <Td>
                        <span className="inline-flex items-center gap-1">
                          {u.email}
                          {rowIsOwner && (
                            <span
                              className="badge-tonal"
                              title="Owner (no editable)"
                            >
                              <MdLock /> Owner
                            </span>
                          )}
                        </span>
                      </Td>
                      <Td>{u.fullName ?? "—"}</Td>
                      <Td>
                        {!locked ? (
                          <RoleSelect
                            value={u.role ?? null}
                            onChange={(r) => onRoleChangeOne(u.uid, r)}
                          />
                        ) : (
                          <RoleBadge role={u.role ?? null} />
                        )}
                      </Td>
                      <td className="px-3 py-2 align-top text-right">
                        <button
                          className="btn btn-outline flex items-center gap-1"
                          onClick={() => openDrawer(u)}
                          disabled={locked}
                          title={
                            locked
                              ? "No tienes permisos para editar este usuario"
                              : "Editar"
                          }
                        >
                          <MdEdit /> Editar
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-10 text-center text-on-surface/70"
                  >
                    <div className="mx-auto max-w-md">
                      <div className="mb-2 text-3xl text-on-surface/50">😶‍🌫️</div>
                      <p className="text-sm">
                        Sin resultados. Ajusta la búsqueda o el rol.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            className="btn btn-outline"
            onClick={() => load(false)}
            disabled={loading || !cursor}
          >
            {loading ? "Cargando…" : cursor ? "Cargar más" : "No hay más"}
          </button>
        </div>
      </section>

      <UserEditorDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        user={selectedUser}
        canEdit={!!selectedUser && uiCanEditRow(selectedUser)}
        onSaved={(patch) => {
          const uid = selectedUser?.uid ?? null;
          if (!uid) return;
          setRows((r) =>
            r.map((x) => (x.uid === uid ? { ...x, ...patch } : x))
          );
        }}
      />
    </AppShell>
  );
}

/* helpers de tabla */
function Th({ children }: { children: React.ReactNode }): React.ReactElement {
  return <th className="px-3 py-2">{children}</th>;
}
function Td({ children }: { children: React.ReactNode }): React.ReactElement {
  return <td className="px-3 py-2 align-top">{children}</td>;
}
